import React, { FC, useMemo, useState, useCallback, useRef, useEffect } from 'react';

import { Divider, Typography } from '@mui/material';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { TaskItem } from '@demind-inc/core';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

import './TaskPageContent.scss';
import { TasksAccordion } from './TasksAccordion';
import {
  taskFilterAtom,
  taskSidebarFiltersAtom,
  useOverdueTasks,
  useTodoTasks,
  useTodoTasksContext,
  useTaskActions,
} from '../../data-access';
import { getDateRange, getDueDateYYYYMMDD, getIsOverdue } from '../../helpers';
import { TabHeader, TaskSortOption } from '../common';
import { useMoveTask, useAutoScrollOnDrag } from '../../hooks';
import { TaskDragPreview } from './TaskDragPreview';

interface TaskPageContentProps {
  className?: string;
}

const TASK_PAGE_DAYS_PAGE_SIZE = 7;
const TASK_PAGE_OVERDUE_LIMIT = 100;
const TASK_PAGE_BOTTOM_THRESHOLD_PX = 120;

const TaskPageContent: FC<TaskPageContentProps> = ({ className }) => {
  const { t } = useTranslation();
  const { visibleProjectIds } = useTodoTasksContext();
  const taskFilter = useRecoilValue(taskFilterAtom);
  const taskSidebarFilters = useRecoilValue(taskSidebarFiltersAtom);
  const taskViewFilter = taskSidebarFilters.view;
  const taskPriorityFilter = taskSidebarFilters.priority;
  const { updateTodoTask } = useTaskActions();
  const { handleMoveTask } = useMoveTask();

  const taskPageStartDate = useMemo(() => dayjs().startOf('day'), []);
  const [taskSortOption, setTaskSortOption] = useState<TaskSortOption | null>('due_date_desc');
  const [taskPageEndDate, setTaskPageEndDate] = useState(() =>
    taskPageStartDate.add(TASK_PAGE_DAYS_PAGE_SIZE - 1, 'day')
  );

  const visibleTaskPageDates = useMemo(
    () => getDateRange({ startDate: taskPageStartDate, endDate: taskPageEndDate }),
    [taskPageStartDate, taskPageEndDate]
  );

  const { todoTasksByDate, isDateLoading } = useTodoTasks({
    todoTasksBoardIds: visibleProjectIds,
    dates: visibleTaskPageDates.map((date) => date.format('YYYY-MM-DD')),
    filter: {
      labelNames: taskFilter.labelNames,
      onlyMyTask: taskFilter.onlyMyTask,
    },
  });

  const { data: overdueTasks = [], isLoading: isOverdueTasksLoading } = useOverdueTasks({
    visibleProjectIds,
    dueDateTill: taskPageStartDate.subtract(1, 'day').format('YYYY-MM-DD'),
    limit: TASK_PAGE_OVERDUE_LIMIT,
    filter: {
      labelNames: taskFilter.labelNames,
      hideNoDueDate: taskFilter.hideNoDueDate,
      onlyMyTask: taskFilter.onlyMyTask,
    },
  });

  const isManualSort = taskSortOption === 'manual';

  const sortTasks = useCallback(
    (tasks: TaskItem[]) => {
      if (!taskSortOption) {
        return tasks;
      }

      if (taskSortOption === 'manual') {
        const getFallbackSortValue = (task: TaskItem) => task.sortIndex ?? Number.MAX_SAFE_INTEGER;
        return [...tasks].sort((a, b) => getFallbackSortValue(a) - getFallbackSortValue(b));
      }

      const getPriorityRank = (priority?: TaskItem['priority']) => {
        if (priority === 'high') return 0;
        if (priority === 'medium') return 1;
        if (priority === 'low') return 2;
        return 3;
      };

      const getDueDateSortValue = (task: TaskItem) => {
        if (task.dueDateTime?.datetime) {
          return dayjs(task.dueDateTime.datetime).valueOf();
        }

        const dueDate = getDueDateYYYYMMDD(task.dueDateTime);
        return dueDate ? dayjs(dueDate).valueOf() : null;
      };

      const getFallbackSortValue = (task: TaskItem) => task.sortIndex ?? Number.MAX_SAFE_INTEGER;

      return [...tasks].sort((taskA, taskB) => {
        if (taskSortOption === 'alphabetical') {
          const byName = (taskA.name || '').localeCompare(taskB.name || '', undefined, {
            sensitivity: 'base',
          });
          if (byName !== 0) {
            return byName;
          }
          return getFallbackSortValue(taskA) - getFallbackSortValue(taskB);
        }

        if (taskSortOption === 'priority') {
          const byPriority = getPriorityRank(taskA.priority) - getPriorityRank(taskB.priority);
          if (byPriority !== 0) {
            return byPriority;
          }
          return getFallbackSortValue(taskA) - getFallbackSortValue(taskB);
        }

        const dueDateA = getDueDateSortValue(taskA);
        const dueDateB = getDueDateSortValue(taskB);

        if (dueDateA === null && dueDateB !== null) {
          return 1;
        }
        if (dueDateA !== null && dueDateB === null) {
          return -1;
        }
        if (dueDateA !== null && dueDateB !== null && dueDateA !== dueDateB) {
          return taskSortOption === 'due_date_asc' ? dueDateA - dueDateB : dueDateB - dueDateA;
        }

        return getFallbackSortValue(taskA) - getFallbackSortValue(taskB);
      });
    },
    [taskSortOption]
  );

  const filterByPriority = useCallback(
    (tasks: TaskItem[]) => {
      const filteredTasks = !taskPriorityFilter
        ? tasks
        : tasks.filter((task) => task.priority === taskPriorityFilter);

      return sortTasks(filteredTasks);
    },
    [taskPriorityFilter, sortTasks]
  );

  const tasksByDate = useMemo(() => {
    const baseTasksByDate = new Map(todoTasksByDate.map((item) => [item.date, item.tasks ?? []]));

    return new Map(
      Array.from(baseTasksByDate.entries()).map(([date, tasks]) => [date, filterByPriority(tasks)])
    );
  }, [todoTasksByDate, filterByPriority]);

  const { overdueTasksWithDueDate, backlogTasks } = useMemo(() => {
    const overdueTasksWithDueDateUnfiltered: TaskItem[] = [];
    const backlogTasksUnfiltered: TaskItem[] = [];

    overdueTasks.forEach((task) => {
      if (getDueDateYYYYMMDD(task.dueDateTime)) {
        overdueTasksWithDueDateUnfiltered.push(task);
        return;
      }

      backlogTasksUnfiltered.push(task);
    });

    const shouldUseUpdatedAtForBacklogSort =
      !taskSortOption || taskSortOption === 'due_date_desc' || taskSortOption === 'due_date_asc';

    return {
      overdueTasksWithDueDate: filterByPriority(overdueTasksWithDueDateUnfiltered),
      backlogTasks: shouldUseUpdatedAtForBacklogSort
        ? [...filterByPriority(backlogTasksUnfiltered)].sort((taskA, taskB) => {
            const updatedAtA = Date.parse(taskA.updatedAt ?? '') || 0;
            const updatedAtB = Date.parse(taskB.updatedAt ?? '') || 0;

            return taskSortOption === 'due_date_asc'
              ? updatedAtA - updatedAtB
              : updatedAtB - updatedAtA;
          })
        : filterByPriority(backlogTasksUnfiltered),
    };
  }, [overdueTasks, filterByPriority, taskSortOption]);

  const todayTasks = useMemo(() => {
    return tasksByDate.get(taskPageStartDate.format('YYYY-MM-DD')) || [];
  }, [tasksByDate, taskPageStartDate]);

  const thisWeekTasks = useMemo(() => {
    const sortedTasks = sortTasks(
      visibleTaskPageDates
        .filter((date) => date.isSame(taskPageStartDate, 'week'))
        .flatMap((date) => tasksByDate.get(date.format('YYYY-MM-DD')) || [])
    );
    return sortedTasks;
  }, [visibleTaskPageDates, taskPageStartDate, tasksByDate]);

  const hasOverdueTasks = overdueTasksWithDueDate.length > 0;
  const hasBacklogTasks = backlogTasks.length > 0;
  const shouldShowOverdueSection = hasOverdueTasks || isOverdueTasksLoading;
  const shouldShowBacklogSection = hasBacklogTasks || isOverdueTasksLoading;
  const isAllTasksView = taskViewFilter === 'all';

  const accordionListContainerRef = useRef<HTMLDivElement>(null);
  const isPaginatingRef = useRef(false);

  useAutoScrollOnDrag(accordionListContainerRef);

  const loadMoreTaskPageDates = useCallback(() => {
    setTaskPageEndDate((prev) => prev.add(TASK_PAGE_DAYS_PAGE_SIZE, 'day'));
  }, []);

  const handleScrollToTop = useCallback(() => {
    const accordionListContainer = accordionListContainerRef.current;
    if (!accordionListContainer) {
      return;
    }

    accordionListContainer.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    isPaginatingRef.current = false;
  }, [visibleTaskPageDates.length, taskViewFilter]);

  const handleAccordionListContainerScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!isAllTasksView) {
        return;
      }

      const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
      const remainingScrollDistance = scrollHeight - scrollTop - clientHeight;

      if (remainingScrollDistance <= TASK_PAGE_BOTTOM_THRESHOLD_PX && !isPaginatingRef.current) {
        isPaginatingRef.current = true;
        loadMoreTaskPageDates();
      }
    },
    [isAllTasksView, loadMoreTaskPageDates]
  );

  useEffect(() => {
    if (!isAllTasksView) {
      return;
    }

    const container = accordionListContainerRef.current;
    if (!container || isPaginatingRef.current) {
      return;
    }

    if (container.scrollHeight <= container.clientHeight) {
      isPaginatingRef.current = true;
      loadMoreTaskPageDates();
    }
  }, [
    isAllTasksView,
    visibleTaskPageDates.length,
    hasOverdueTasks,
    hasBacklogTasks,
    todoTasksByDate.length,
    loadMoreTaskPageDates,
  ]);

  const switchToManualSort = useCallback(() => {
    if (taskSortOption !== 'manual') {
      setTaskSortOption('manual');
    }
  }, [taskSortOption]);

  const handleReorderTask = useCallback(
    async (task: TaskItem, newSortIndex: number) => {
      try {
        await updateTodoTask({
          boardId: task.boardId,
          taskId: task.taskId,
          newTaskInfo: { ...task, sortIndex: newSortIndex },
          shouldReflectToEvent: false,
        });
        switchToManualSort();
      } catch (error) {
        console.error('Failed to reorder task:', error);
      }
    },
    [updateTodoTask, switchToManualSort]
  );

  const createMoveHandler = useCallback(
    (targetDate: dayjs.Dayjs) => (task: TaskItem, sortIndex: number) => {
      const prevDate = getDueDateYYYYMMDD(task.dueDateTime);
      handleMoveTask({
        task,
        targetDate,
        sortIndex,
        wasOverdue: prevDate ? getIsOverdue(prevDate) : false,
      });
      switchToManualSort();
    },
    [handleMoveTask, switchToManualSort]
  );

  return (
    <div className={clsx('task-page-content', className)}>
      <TabHeader
        mode="task"
        onDateRangeClick={handleScrollToTop}
        taskSortOption={taskSortOption}
        onTaskSortChange={setTaskSortOption}
      />
      <Divider />
      <div
        ref={accordionListContainerRef}
        className="task-page-content__accordion-list-container"
        onScroll={handleAccordionListContainerScroll}
      >
        <Typography variant="h3" className="task-page-content__title">
          {t('task.title')}
        </Typography>
        <TaskDragPreview />
        <div className="task-page-content__accordion-list">
          {taskViewFilter === 'all' && (
            <>
              {shouldShowOverdueSection && (
                <TasksAccordion
                  title={t('task.sections.overdue')}
                  sectionType="overdue"
                  tasks={overdueTasksWithDueDate}
                  loading={isOverdueTasksLoading}
                />
              )}
              {shouldShowBacklogSection && (
                <TasksAccordion
                  title={t('task.sections.backlog')}
                  sectionType="backlog"
                  tasks={backlogTasks}
                  loading={isOverdueTasksLoading}
                />
              )}
              {visibleTaskPageDates.map((date) => {
                const dateKey = date.format('YYYY-MM-DD');
                return (
                  <TasksAccordion
                    key={dateKey}
                    date={date}
                    tasks={tasksByDate.get(dateKey) || []}
                    loading={isDateLoading(dateKey)}
                    defaultExpanded={date.isSame(taskPageStartDate, 'day')}
                    onMoveTask={createMoveHandler(date)}
                    onReorderTask={handleReorderTask}
                  />
                );
              })}
            </>
          )}

          {taskViewFilter === 'today' && (
            <TasksAccordion
              date={taskPageStartDate}
              tasks={todayTasks}
              loading={isDateLoading(taskPageStartDate.format('YYYY-MM-DD'))}
              defaultExpanded
              onMoveTask={createMoveHandler(taskPageStartDate)}
              onReorderTask={handleReorderTask}
            />
          )}

          {taskViewFilter === 'this_week' && (
            <TasksAccordion
              date={taskPageStartDate}
              title={t('task.sections.thisWeek')}
              sectionType="date"
              tasks={thisWeekTasks}
              loading={visibleTaskPageDates
                .filter((d) => d.isSame(taskPageStartDate, 'week'))
                .some((d) => isDateLoading(d.format('YYYY-MM-DD')))}
              defaultExpanded
              onReorderTask={handleReorderTask}
            />
          )}

          {taskViewFilter === 'overdue' && (
            <TasksAccordion
              title={t('task.sections.overdue')}
              sectionType="overdue"
              tasks={overdueTasksWithDueDate}
              loading={isOverdueTasksLoading}
              defaultExpanded
            />
          )}

          {taskViewFilter === 'backlog' && (
            <TasksAccordion
              title={t('task.sections.backlog')}
              sectionType="backlog"
              tasks={backlogTasks}
              loading={isOverdueTasksLoading}
              defaultExpanded
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskPageContent;
